import { OrderFormInterface, Restrictions } from '@galaxy/marketplace-apps/v1';
import { AccountAccountStatus } from '@vendasta/accounts/legacy';
import {
  Categories,
  Currency as CurrencyEnum,
  LmiCategory,
  LMI_CATEGORIES,
  LMI_CATEGORY_MAP,
} from '@vendasta/marketplace-packages';
import { Faq, Product as StoreProduct } from '@vendasta/store';
import { Edition } from '../../activation/core/interface';
import { DiscoverableItem } from '../discoverable-item/discoverable-item.model';
import { ProductOrigin } from '../enums';
import { BillingFrequency, Currency } from '../types';
import { Updatable } from '../utils/array-utils';

export class Product implements Updatable {
  constructor(data: any) {
    this.name = data.name;
    this.tagline = data.tagline;
    this.origin = data.origin;
    this.product_id = data.productId || data.product_id;
    this.edition_name = data.editionName || data.edition_name;
    this.partner_id = data.partnerId || data.partner_id;
    this.description = data.description;
    this.category = data.category;
    this.faqs = data.faqs;
    this.lmi_categories = this.getLmiCategory(data);
    this.service_model = data.serviceModel || data.service_model;
    this.header_image_url = data.headerImageUrl || data.header_image_url;
    this.icon_url = data.iconUrl || data.icon_url || data.icon;
    this.icon_color = data.iconColor || data.icon_color;
    this.screenshot_urls = data.screenshotUrls || data.screenshot_urls;
    this.pdf_upload_urls = data.pdfUploadUrls || data.pdf_upload_urls;
    this.recommended_sell_price = data.recommendedSellPrice || data.recommended_sell_price;
    this.wholesale_price = data.wholesalePrice || data.wholesale_price;
    this.setup_fee = data.setupFee || data.setup_fee;
    this.enabled = data.enabled;
    this.billing_frequency = this.getBillingFrequency(data);
    this.billing_frequency_other = this.getBillingFrequency(data);
    this.currency = this.getCurrency(data);
    this.hide_product_icons_and_names = data.hideProductIconsAndNames || data.hide_product_icons_and_names;
    this.hide_product_details = data.hideProductDetails || data.hide_product_details;
    this.uses_customization_form = data.usesCustomizationForm || data.uses_customization_form;
    this.key_selling_points = data.keySellingPoints || data.key_selling_points;
    this.entry_url = data.entryUrl || data.entry_url;
    this.website_url = data.websiteUrl || data.website_url;
    this.createdDate = data.created ? new Date(data.created) : null;
    this.updatedDate = data.created ? new Date(data.updated) : null;
    this.suspendedDate = data.suspended ? new Date(data.suspended) : null;
    this.order_form = data.OrderForm || data.order_form;
    this.restrictions = data.restrictions;
    this.billing_id = data.billingId || data.billing_id;
    this.trial = data.trial;
    this.uses_activation_specific_entry_url =
      data.uses_activation_specific_entry_url || data.usesActivationSpecificEntryUrl;
    this.usesCustomPricing = data.uses_custom_pricing || data.usesCustomPricing;
    this.editionId = data.editionId;
    this.isSuspended = data.isSuspended;
    this.editions = data.editions || [];
    /* TODO: Fix this constructor. For now don't change behaviour unless the constructor is called with an
        anniversery or commitment date.
     */
    this.anniversaryDate = data.anniversaryDate ? new Date(data.anniversaryDate) : undefined;
    this.commitmentDate = data.commitmentDate ? new Date(data.commitmentDate) : undefined;
  }

  get isRmProduct(): boolean {
    return this.product_id === 'RM';
  }

  get isCustom(): boolean {
    return this.origin === ProductOrigin[ProductOrigin.partner_created];
  }

  get dollarWholesalePrice(): number {
    return this.wholesale_price / 100;
  }
  name: string;
  tagline: string;
  origin: string;
  product_id: string;
  edition_name?: string;
  partner_id: string;
  description: string;
  category: string;
  faqs: Faq[];
  lmi_categories: LmiCategory[];
  service_model: string[];
  header_image_url: string;
  icon_url: string;
  icon_color: string;
  screenshot_urls: string[];
  pdf_upload_urls: string[];
  recommended_sell_price: number;
  wholesale_price: number;
  setup_fee: number;
  enabled: boolean;
  billing_frequency: BillingFrequency;
  billing_frequency_other: string;
  currency: Currency;
  hide_product_icons_and_names: boolean;
  hide_product_details: boolean;
  uses_customization_form: boolean;
  uses_activation_specific_entry_url: boolean;
  usesCustomPricing: boolean;
  key_selling_points: string[];
  entry_url: string;
  website_url: string;
  trial: boolean;
  editionId?: string;
  editions: Edition[]; //list of available editions for the product.
  isSuspended: boolean;

  status: AccountAccountStatus;
  order_form: {
    label: string;
    id: string;
    type: string;
    required: boolean;
    options: any;
    description: string;
    uploadUrl?: string;
  }[];
  restrictions?: { country?: { whitelist?: string[]; blacklist?: string[] } };
  billing_id: string;

  createdDate: Date;
  updatedDate: Date;
  anniversaryDate: Date;
  commitmentDate: Date;

  suspendedDate: Date;

  static getProductFromDiscoverableItem(item: DiscoverableItem): Product {
    const product = new Product({});
    product.product_id = item.appId;
    product.name = item.name;
    product.icon_url = item.icon;
    product.tagline = item.tagline;
    product.header_image_url = item.headerImage;
    product.currency = item.currency as Currency;
    product.wholesale_price = item.price;
    product.billing_frequency = Product.getBillingFrequency(item.billingFrequency);
    product.lmi_categories = item.lmiCategories ? item.lmiCategories.map((category) => LMI_CATEGORIES[category]) : null;
    product.origin = item.isCustom
      ? ProductOrigin[ProductOrigin.partner_created]
      : ProductOrigin[ProductOrigin.marketplace_app];
    product.enabled = item.enabled;
    return product;
  }

  static getBillingFrequency(billingFrequency: string): BillingFrequency {
    if (!billingFrequency) {
      return 'monthly';
    }
    if (billingFrequency.toLowerCase() === 'one time') {
      return 'once';
    }
    return billingFrequency as BillingFrequency;
  }

  static addOrderFormFieldsToProduct(product: Product, orderFormContainer: OrderFormInterface): Product {
    if (!orderFormContainer || !product) {
      return null;
    }
    product.order_form = [];
    if (orderFormContainer.orderForm) {
      orderFormContainer.orderForm.forEach((field) => {
        const customFormField = {
          label: field.label,
          id: field.id,
          type: field.type,
          required: field.required,
          options: field.options,
          description: field.description,
        };
        product.order_form.push(customFormField);
      });
    }
    return product;
  }

  static buildOrderFormForCustomProduct(product: Product): Product {
    if (product.origin === 'partner_created' && product.uses_customization_form) {
      product.order_form = [
        {
          id: 'custom-entry-url-' + product.product_id,
          label: 'Custom Entry Url',
          type: 'text',
          required: true,
          description: '',
          options: null,
        },
      ];
    }
    return product;
  }

  static isCustomProductBasedOnId(id: string): boolean {
    return id.startsWith('CP-');
  }

  convertToStoreProduct(): StoreProduct {
    const p = new StoreProduct();
    p.productId = this.product_id;
    p.editionId = this.editionId;
    p.editionName = this.edition_name;
    p.name = this.name;
    p.description = this.description;
    p.tagline = this.tagline;
    p.iconUrl = this.icon_url;
    p.entryUrl = this.entry_url;
    p.screenshotUrls = this.screenshot_urls;
    p.pdfUploadUrls = this.pdf_upload_urls;
    p.lmiCategories = this.getLmiCategoryNames();
    p.keySellingPoints = this.key_selling_points;
    p.faqs = this.faqs;
    p.wholesalePrice = this.wholesale_price;
    p.currency = this.currency;
    p.billingFrequency = this.billing_frequency;
    p.billingId = this.billing_id;
    p.restrictions = this.restrictions as Restrictions;
    return p;
  }

  private getLmiCategory(data: any): LmiCategory[] {
    if (data.lmiCategories) {
      return data.lmiCategories.map((cat) => LMI_CATEGORY_MAP[Categories[cat].toLowerCase()]);
    } else if (data.lmi_categories) {
      return data.lmi_categories.map((cat) => LMI_CATEGORY_MAP[cat]);
    } else {
      return [];
    }
  }

  private getBillingFrequency(data: any): BillingFrequency {
    let billing_frequency: string;
    billing_frequency = data.billingFrequency || data.billing_frequency;
    if (!billing_frequency) {
      billing_frequency = data.billingFrequencyOther || data.billing_frequency_other;
    }
    if (billing_frequency === 'one time') {
      billing_frequency = 'once';
    }
    return billing_frequency as BillingFrequency;
  }

  private getCurrency(data: any): Currency {
    if (data.currency) {
      if (typeof data.currency !== 'string') {
        return CurrencyEnum[data.currency] as Currency;
      } else {
        return data.currency as Currency;
      }
    } else {
      return CurrencyEnum[0] as Currency;
    }
  }

  getLmiCategoryNames(): string[] {
    return this.lmi_categories.map((lmi) => lmi.name);
  }
}

export class ProductFormValues {
  productId?: string;
  name: string;
  tagline: string;
  usesCustomizationFormFlag: boolean;
  entryUrl: string;
  websiteUrl: string;
  currency: Currency;
  wholesalePrice: number;
  recommendedSellPrice: number;
  billingFrequency: BillingFrequency;
  headerImageUrl: string;
  iconUrl: string;
  serviceModel: string[];
  faqsJson: any;
  keySellingPoints: string[];
  description: string;
  screenshotUrls: string[];
  pdfUploadUrls: string[];
}

export class ActivatedProduct extends Product {
  activationId: string;
  descriptor: string;
  orderFormSubmissionId: string;
  deactivationIsEditionChange: boolean;
  deactivationNewEditionId: string;
  deactivationNewBillingOrderId: string;
  appLevelAdminDashboard: boolean;

  constructor(data: any) {
    super(data);
    Object.assign(this, data);
  }
}
